import { render, waitFor, fireEvent } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { expect } from 'chai';
import {
  getRoot,
  populateData,
  TestComponentsProps,
  getContents,
  mouseItemSelection
} from './testHelpers';

export const testSelectionRender = ({
  SelectionCardGrid
}: TestComponentsProps<string | number>) => {
  it('multiple selection - multiple selected items can be preserved', async () => {
    const data = populateData(3);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();

    mouseItemSelection(items[0]);
    mouseItemSelection(items[1]);
    await waitFor(() => {
      expect(items[0].getAttribute('aria-selected')).eq('true');
      expect(items[1].getAttribute('aria-selected')).eq('true');
    });
  });

  it('range selection - space and then shift+space', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // press space to select the 1st item
    fireEvent.keyUp(items[0], { key: ' ' });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // arrow right to the 3rd item, then shift + space the 3rd item
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyUp(items[2], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - click and then shift+space', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // click to select the 1st item
    mouseItemSelection(items[0]);
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // arrow right to the 3rd item, then shift + space the 3rd item
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyUp(items[2], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - space and then shift+click', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // press space to select the 1st item
    fireEvent.keyUp(items[0], { key: ' ' });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + click the 3rd item
    mouseItemSelection(items[2], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - click and then shift+click', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();

    // click to select the 1st item
    mouseItemSelection(items[0]);
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + click the 3rd item
    mouseItemSelection(items[2], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - from current item to shift+click item', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + click the 3rd item
    fireEvent.click(items[2], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - from current item to shift+arrow items', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + arrow to the 3rd item
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - from current item to shift+click and then shift+click', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + click the 2nd item
    fireEvent.click(items[1], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + click the 4th item
    fireEvent.click(items[3], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - from current item to shift+click and then shift+arrow  ', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + click the 2nd item
    fireEvent.click(items[1], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + arrow to the 4th item
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - from current item to shift+click and then shift+space', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + click the 2nd item
    fireEvent.click(items[1], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + space the 4th item
    fireEvent.keyUp(items[3], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('false');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - from current item to shift+arrow and then shift+click', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + arrow to the 2nd item
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // shift + click the 4th item
    fireEvent.click(items[3], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - from current item to shift+arrow and then shift+arrow', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + arrow to the 2nd item
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // arrow to the 3rd item, then shift + arrow to the 4th item
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyDown(grid, { key: 'ArrowRight' });
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('false');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - from current item to shift+arrow and then shift+space', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + arrow to the 2nd item
    fireEvent.click(items[0]);
    fireEvent.keyDown(grid, { key: 'ArrowRight', shiftKey: true });
    await waitFor(() => {
      expect(items[0].getAttribute('aria-selected')).eq('true');
      expect(items[1].getAttribute('aria-selected')).eq('true');
      expect(items[2].getAttribute('aria-selected')).eq('false');
      expect(items[3].getAttribute('aria-selected')).eq('false');
    });

    // shift + space the 4th item
    fireEvent.keyUp(items[3], { key: ' ', shiftKey: true });
    await waitFor(() => {
      expect(items[0].getAttribute('aria-selected')).eq('false');
      expect(items[1].getAttribute('aria-selected')).eq('true');
      expect(items[2].getAttribute('aria-selected')).eq('true');
      expect(items[3].getAttribute('aria-selected')).eq('true');
    });
  });

  it('range selection - from current item to shift+space and then shift+space', async () => {
    const data = populateData(4);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();

    // tab and focus to make the 1st item current
    await userEvent.tab();
    expect(grid.matches(':focus')).to.be.true;

    // shift + space to select the 1st item
    fireEvent.keyUp(items[0], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('false');
    expect(items[3].getAttribute('aria-selected')).eq('false');

    // arrow down to the 3rd item, then shift + space the 3rd item
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyDown(grid, { key: 'ArrowDown' });
    fireEvent.keyUp(items[2], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
  });

  it('range selection - shift+space and then shift+space', async () => {
    const data = populateData(5);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();
    mouseItemSelection(items[0]);

    // shift + space to select the 2nd item
    fireEvent.keyUp(items[2], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
    expect(items[4].getAttribute('aria-selected')).eq('false');

    // shift + space the last item
    fireEvent.keyUp(items[4], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
  });

  it('range selection - deselect selected item by shift+space', async () => {
    const data = populateData(3);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();
    mouseItemSelection(items[0]);

    // shift + space to select the 2nd item
    fireEvent.keyUp(items[2], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');

    fireEvent.keyUp(items[1], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
  });

  it('range selection backward - shift+space and then shift+space', async () => {
    const data = populateData(5);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();
    mouseItemSelection(items[2]);

    // shift + space to select the 1st item
    fireEvent.keyUp(items[0], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
    expect(items[4].getAttribute('aria-selected')).eq('false');

    // shift + space the last item
    fireEvent.keyUp(items[4], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('false');
    expect(items[1].getAttribute('aria-selected')).eq('false');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
  });

  it('range selection backward - shift+click and then shift+space', async () => {
    const data = populateData(5);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const items = await getContents();
    mouseItemSelection(items[2]);

    // shift + click to select the 1st item
    mouseItemSelection(items[0], { shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
    expect(items[4].getAttribute('aria-selected')).eq('false');

    // shift + space the last item
    fireEvent.keyUp(items[4], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
  });

  it('range selection backward - shift+arrow and then shift+space', async () => {
    const data = populateData(5);
    render(<SelectionCardGrid data={data} selectionMode={'multiple'}></SelectionCardGrid>);

    const grid = await getRoot();
    const items = await getContents();
    mouseItemSelection(items[2]);

    // shift + arrow to select the 1st item
    fireEvent.keyDown(grid, { key: 'ArrowLeft', shiftKey: true });
    fireEvent.keyDown(grid, { key: 'ArrowLeft', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('false');
    expect(items[4].getAttribute('aria-selected')).eq('false');

    // shift + space the last item
    fireEvent.keyUp(items[4], { key: ' ', shiftKey: true });
    expect(items[0].getAttribute('aria-selected')).eq('true');
    expect(items[1].getAttribute('aria-selected')).eq('true');
    expect(items[2].getAttribute('aria-selected')).eq('true');
    expect(items[3].getAttribute('aria-selected')).eq('true');
    expect(items[4].getAttribute('aria-selected')).eq('true');
  });
};
